import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import "../styles/ProductCard.css";
import { formatPrice } from "../utils/formatPrice";

const EweProductCard = ({
  product,
  onAddToCart,
  refreshCart,
  isLoggedIn,
  handleGlobalAddToCart,
  user,
  guestCartId
}) => {
  const navigate = useNavigate();
  const [imgSrc, setImgSrc] = useState('');
  const [errorCount, setErrorCount] = useState(0);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [isAddingToCart, setIsAddingToCart] = useState(false);
  const [showAddedNotification, setShowAddedNotification] = useState(false);
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  // Get clean product code for URL construction
  const getCleanProductCode = () => {
    return product.acProduct?.replace(/[^a-zA-Z0-9]/g, '') || '';
  };

  // Get the correct product image
  const getProductImage = () => {
    const cleanCode = getCleanProductCode();

    // Try constructed URL from product code first
    if (cleanCode) {
      return `https://resource.ewe.rs/products/${cleanCode}.jpg`;
    }

    // Then try direct image URLs from product data
    if (product.urlImages?.length > 0) {
      const mainProductImage = product.urlImages.find(img =>
        img.acType === "image" && (img.acImage || img.acThumbnail)
      );

      if (mainProductImage) {
        return mainProductImage.acImage || mainProductImage.acThumbnail;
      }
    }

    // Final fallback
    return '/noImage.jpg';
  };

  // Handle image loading errors with improved fallback logic
  const handleImageError = () => {
    const cleanCode = getCleanProductCode();
    const fallbacks = [
      // Try alternative image URLs if we have a product code
      ...(cleanCode ? [
        `https://resource.ewe.rs/products/${cleanCode}_v.jpg`,
        `https://resource.ewe.rs/products/${cleanCode}_1.jpg`
      ] : []),
      // Final fallback
      '/noImage.jpg'
    ];

    const currentIndex = fallbacks.indexOf(imgSrc);
    const nextIndex = currentIndex === -1 ? 0 : currentIndex + 1;

    if (nextIndex < fallbacks.length) {
      setImgSrc(fallbacks[nextIndex]);
    } else {
      setImgSrc('/noImage.jpg');
    }
  };

  // Initialize image source
  useEffect(() => {
    const url = getProductImage();
    setImgSrc(url || '/noImage.jpg');
    setErrorCount(0);
    setImageLoaded(false);
  }, [product]);

  // Product status flags
  const isNew = product.anNew === 1;
  const isBestBuy = product.anBestBuy === 1;
  const isRecommended = product.anAdvice === 1;
  const isOnSale = product.isPromotion === 1 || (product.anDiscount || 0) > 0;
  const isOutOfStock = (product.anStock || 0) <= 0;

  // Price calculations
  const price = product.anPrice || 0;
  const retailPrice = product.anRetailPrice || 0;
  const displayPrice = () => {
    if (retailPrice <= 0) {
      return price;
    }
    if (price > retailPrice && price > 0) {
      return price;
    }
    return retailPrice;
  };

  const discount = product.anDiscount || 0;
  const finalPrice = displayPrice();
  const salePrice = discount > 0 ? retailPrice * (1 - discount / 100) : retailPrice;

  const handleCardClick = () => {
    if (window.innerWidth <= 768) {
      navigate(`/home/product/${product.anProductKey || product.id}`);
    }
  };

 const handleAddToCart = async (e) => {
   e.stopPropagation();

   if (product.anStock <= 0) {
     alert('Ovaj proizvod nije dostupan');
     return;
   }

   setIsAddingToCart(true);

   try {
     // Get authentication data
     const token = localStorage.getItem('token');
     const storedUser = localStorage.getItem('user');
     const currentUser = user || (storedUser ? JSON.parse(storedUser) : null);

     // Improved authentication logic
     // Consider user logged in if they have a valid user ID, regardless of token status
     // (token might be expired but user session might still be valid)
     const userIsLoggedIn = !!(currentUser?.id);

     // However, for API calls, we still need to check if we should use the token
     const hasValidToken = !!token;

     console.log('Auth check:', {
       hasToken: hasValidToken,
       hasUser: !!currentUser,
       userId: currentUser?.id,
       guestCartId: guestCartId,
       userIsLoggedIn,
       storedUser: !!storedUser
     });

     const productId = product.id || product.anProductKey;
     if (!productId) {
       throw new Error('Invalid product ID');
     }

     // Build the payload
     const payload = {
       productId: productId,
       productKey: product.anProductKey,
       productCode: product.acProduct,
       productName: product.acName,
       quantity: 1,
       price: product.anPrice || product.price,
       imageUrl: product.urlImages?.[0]?.url || null,
       retailPrice: product.anRetailPrice || product.retailPrice
     };

     // Determine which cart to use
     if (userIsLoggedIn) {
       payload.userId = currentUser.id;
       console.log('Adding to user cart:', currentUser.id);
     } else {
       // Ensure we have a guestCartId for guest users
       const currentGuestCartId = guestCartId || localStorage.getItem('guestCartId');
       if (!currentGuestCartId) {
         throw new Error('No guest cart ID available');
       }
       payload.guestCartId = currentGuestCartId;
       console.log('Adding to guest cart:', currentGuestCartId);
     }

     console.log('Cart payload:', payload);

     // Make the API call
     const response = await fetch(`${API_BASE_URL}/cart/add`, {
       method: 'POST',
       credentials: 'include',
       headers: {
         'Content-Type': 'application/json',
         // Only include Authorization header if we have a token AND we're treating user as logged in
         ...(userIsLoggedIn && hasValidToken && {
           'Authorization': `Bearer ${token}`
         })
       },
       body: JSON.stringify(payload)
     });

     if (!response.ok) {
       const errorData = await response.json();
       console.error('Cart add error:', errorData);
       throw new Error(errorData.message || 'Failed to add to cart');
     }

     const result = await response.json();
     console.log('Cart add success:', result);

     // Refresh cart with consistent logic
     if (refreshCart) {
       await refreshCart();
     }

     setShowAddedNotification(true);
     setTimeout(() => setShowAddedNotification(false), 3000);

   } catch (error) {
     console.error('Error adding to cart:', error);

     // If it's an authentication error, you might want to handle it specially
     if (error.message.includes('authentication') || error.message.includes('token')) {
       // Option 1: Clear user data and force guest mode
       localStorage.removeItem('token');
       localStorage.removeItem('user');
       alert('Your session has expired. Please log in again or continue as guest.');
       // Option 2: Retry as guest
       // You could implement retry logic here
     } else {
       alert(`Greška pri dodavanju u korpu: ${error.message}`);
     }
   } finally {
     setIsAddingToCart(false);
   }
 };

  return (
    <div className={`product-card ${isOutOfStock ? 'out-of-stock' : ''}`} onClick={handleCardClick}>
      <div className="image-container">
        <div className="image-wrapper">
          <img
            src={imgSrc || '/noImage.jpg'}
            alt={product.acName || product.name || 'Product image'}
            onError={handleImageError}
            onLoad={() => setImageLoaded(true)}
            loading="lazy"
            className={`product-image ${imageLoaded ? 'loaded' : 'loading'}`}
          />
        </div>

        <div className="badges-container">
          {isNew && <div className="badge new-badge">Novo</div>}
          {isBestBuy && <div className="badge best-buy-badge">Najprodavanije</div>}
          {isRecommended && <div className="badge recommended-badge">Preporučeno</div>}
          {isOnSale && (
            <div className="badge sale-badge">
              {'Akcija'}
            </div>
          )}
          {isOutOfStock && <div className="badge out-of-stock-badge">Rasprodato</div>}
        </div>
      </div>

      <div className="product-info">
        <div className="product-brand">{product.acDept || product.department}</div>
        <h3 className="product-name">{product.acName || product.name}</h3>

        <div className="price-container">
          {isOnSale ? (
            <span className="sale-price">
              {formatPrice(salePrice)} RSD
            </span>
          ) : (
            <span className="price">
              {formatPrice(finalPrice)} RSD
            </span>
          )}
        </div>
      </div>

      <div className="product-buttons">
        {showAddedNotification && (
          <div className="added-to-cart-notification">
            <span>✓ Dodato u korpu</span>
          </div>
        )}
        <button
          className={`buy-button ${isOutOfStock ? 'disabled' : ''}`}
          onClick={handleAddToCart}
          disabled={isOutOfStock || isAddingToCart}
        >
          {isAddingToCart ? 'Dodaje se...' :
            isOutOfStock ? 'Nema na stanju' : 'Dodaj u Korpu'}
        </button>
        <Link
          to={`/home/product/${product.anProductKey || product.id}`}
          className="view-more-button"
          onClick={(e) => e.stopPropagation()}
        >
          Detalji
        </Link>
      </div>
    </div>
  );
};

export default EweProductCard;